/**
 * BRICK Schema Service
 *
 * Centralized service for all BRICK Schema API interactions.
 * Provides consistent error handling and response formatting.
 *
 * @module brickSchemaService
 */

import api from './api';

/**
 * BRICK Schema Service - Centralized API layer for all BRICK Schema operations
 */
export const brickSchemaService = {
  // ===========================
  // Equipment Operations
  // ===========================

  equipment: {
    /**
     * Get all equipment for a site
     * @param {number} siteId - Site ID
     * @param {boolean} bypassCache - Force fresh data from server
     * @returns {Promise<Object>} { success: boolean, data: Array|null, error?: string }
     */
    async getAll(siteId, bypassCache = false) {
      try {
        // Always include devices to get full device data (location_space_id, device_part, etc.)
        const params = bypassCache ? { bypass_cache: 1, include: 'devices' } : { include: 'devices' };
        const response = await api.get(`/v2/sites/${siteId}/equipment`, params);
        return { success: true, data: response };
      } catch (error) {
        return { success: false, data: null, error: error.message };
      }
    },

    /**
     * Get single equipment by ID
     * @param {number} siteId - Site ID
     * @param {number} equipmentId - Equipment ID
     * @returns {Promise<Object>} { success: boolean, data: Object|null, error?: string }
     */
    async getById(siteId, equipmentId) {
      try {
        const response = await api.get(`/v2/sites/${siteId}/equipment/${equipmentId}`);
        return { success: true, data: response };
      } catch (error) {
        return { success: false, data: null, error: error.message };
      }
    },

    /**
     * Create new equipment
     * @param {number} siteId - Site ID
     * @param {Object} equipmentData - Equipment data
     * @returns {Promise<Object>} { success: boolean, data: Object|null, error?: string }
     */
    async create(siteId, equipmentData) {
      try {
        const response = await api.post(`/v2/sites/${siteId}/equipment`, equipmentData);
        return { success: true, data: response };
      } catch (error) {
        return { success: false, data: null, error: error.message };
      }
    },

    /**
     * Update existing equipment
     * @param {number} siteId - Site ID
     * @param {number} equipmentId - Equipment ID
     * @param {Object} equipmentData - Updated equipment data
     * @returns {Promise<Object>} { success: boolean, data: Object|null, error?: string }
     */
    async update(siteId, equipmentId, equipmentData) {
      try {
        const response = await api.put(`/v2/sites/${siteId}/equipment/${equipmentId}`, equipmentData);
        return { success: true, data: response };
      } catch (error) {
        return { success: false, data: null, error: error.message };
      }
    },

    /**
     * Delete equipment
     * @param {number} siteId - Site ID
     * @param {number} equipmentId - Equipment ID
     * @param {boolean} cascade - Delete children recursively
     * @returns {Promise<Object>} { success: boolean, error?: string, response?: Object }
     */
    async delete(siteId, equipmentId, cascade = false) {
      try {
        const params = cascade ? { cascade: true } : {};
        await api.delete(`/v2/sites/${siteId}/equipment/${equipmentId}`, params);
        return { success: true };
      } catch (error) {
        // Preserve full error response for cascade delete handling
        return {
          success: false,
          error: error.message,
          response: {
            status: error.status,
            data: error.data,
          },
        };
      }
    },

    /**
     * Get equipment for a specific space
     * @param {number} siteId - Site ID
     * @param {number} spaceId - Space ID
     * @returns {Promise<Object>} { success: boolean, data: Array|null, error?: string }
     */
    async getBySpace(siteId, spaceId) {
      try {
        const response = await api.get(`/v2/sites/${siteId}/spaces/${spaceId}/equipment`);
        return { success: true, data: response };
      } catch (error) {
        return { success: false, data: null, error: error.message };
      }
    },

    /**
     * Get equipment relationships
     * @param {number} siteId - Site ID
     * @param {number} equipmentId - Equipment ID
     * @returns {Promise<Object>} { success: boolean, data: Object|null, error?: string }
     */
    async getRelationships(siteId, equipmentId) {
      try {
        const response = await api.get(`/v2/sites/${siteId}/equipment/${equipmentId}/relationships`);
        return { success: true, data: response };
      } catch (error) {
        return { success: false, data: null, error: error.message };
      }
    },

    /**
     * Duplicate equipment
     * @param {number} siteId - Site ID
     * @param {number} equipmentId - Equipment ID to duplicate
     * @param {string} newName - Name for duplicated equipment
     * @returns {Promise<Object>} { success: boolean, data: Object|null, error?: string }
     */
    async duplicate(siteId, equipmentId, newName) {
      try {
        const response = await api.post(`/v2/sites/${siteId}/equipment/${equipmentId}/duplicate`, { name: newName });
        return { success: true, data: response };
      } catch (error) {
        return { success: false, data: null, error: error.message };
      }
    },

    /**
     * Save equipment as template
     * @param {number} siteId - Site ID
     * @param {number} equipmentId - Equipment ID
     * @param {Object} templateData - Template data
     * @returns {Promise<Object>} { success: boolean, data: Object|null, error?: string }
     */
    async saveAsTemplate(siteId, equipmentId, templateData) {
      try {
        const response = await api.post(`/v2/sites/${siteId}/equipment/${equipmentId}/template`, templateData);
        return { success: true, data: response };
      } catch (error) {
        return { success: false, data: null, error: error.message };
      }
    },

    /**
     * Sync equipment with template
     * @param {number} siteId - Site ID
     * @param {number} equipmentId - Equipment ID
     * @returns {Promise<Object>} { success: boolean, data: Object|null, error?: string }
     */
    async syncWithTemplate(siteId, equipmentId) {
      try {
        const response = await api.put(`/v2/sites/${siteId}/equipment/${equipmentId}/sync`);
        return { success: true, data: response };
      } catch (error) {
        return { success: false, data: null, error: error.message };
      }
    },
  },

  // ===========================
  // Device Operations
  // ===========================

  devices: {
    /**
     * Get all devices for a site or equipment
     * @param {number} siteId - Site ID
     * @param {Object} params - Query parameters (equipment_id, etc.)
     * @returns {Promise<Object>} { success: boolean, data: Array|null, error?: string }
     */
    async getAll(siteId, params = {}) {
      try {
        const response = await api.get(`/v2/sites/${siteId}/devices`, params);
        return { success: true, data: response };
      } catch (error) {
        return { success: false, data: null, error: error.message };
      }
    },

    /**
     * Get single device by ID
     * @param {number} siteId - Site ID
     * @param {number} deviceId - Device ID
     * @returns {Promise<Object>} { success: boolean, data: Object|null, error?: string }
     */
    async getById(siteId, deviceId) {
      try {
        // Include template to get brick_class constraint for filtering
        const response = await api.get(`/v2/sites/${siteId}/devices/${deviceId}?include=equipment,points,devicePart,template`);
        return { success: true, data: response };
      } catch (error) {
        return { success: false, data: null, error: error.message };
      }
    },

    /**
     * Create new device
     * @param {number} siteId - Site ID
     * @param {Object} deviceData - Device data
     * @returns {Promise<Object>} { success: boolean, data: Object|null, error?: string }
     */
    async create(siteId, deviceData) {
      try {
        const response = await api.post(`/v2/sites/${siteId}/devices`, deviceData);
        return { success: true, data: response };
      } catch (error) {
        return { success: false, data: null, error: error.message };
      }
    },

    /**
     * Update existing device
     * @param {number} siteId - Site ID
     * @param {number} deviceId - Device ID
     * @param {Object} deviceData - Updated device data
     * @returns {Promise<Object>} { success: boolean, data: Object|null, error?: string }
     */
    async update(siteId, deviceId, deviceData) {
      try {
        const response = await api.put(`/v2/sites/${siteId}/devices/${deviceId}`, deviceData);
        return { success: true, data: response };
      } catch (error) {
        return { success: false, data: null, error: error.message };
      }
    },

    /**
     * Delete device
     * @param {number} siteId - Site ID
     * @param {number} deviceId - Device ID
     * @param {boolean} cascade - Delete children recursively
     * @returns {Promise<Object>} { success: boolean, error?: string, response?: Object }
     */
    async delete(siteId, deviceId, cascade = false) {
      try {
        const params = cascade ? { cascade: true } : {};
        await api.delete(`/v2/sites/${siteId}/devices/${deviceId}`, params);
        return { success: true };
      } catch (error) {
        // Preserve full error response for cascade delete handling
        return {
          success: false,
          error: error.message,
          response: {
            status: error.status,
            data: error.data,
          },
        };
      }
    },

    /**
     * Duplicate device
     * @param {number} siteId - Site ID
     * @param {number} deviceId - Device ID to duplicate
     * @param {string} newName - Name for duplicated device
     * @returns {Promise<Object>} { success: boolean, data: Object|null, error?: string }
     */
    async duplicate(siteId, deviceId, newName) {
      try {
        const response = await api.post(`/v2/sites/${siteId}/devices/${deviceId}/duplicate`, { name: newName });
        return { success: true, data: response };
      } catch (error) {
        return { success: false, data: null, error: error.message };
      }
    },

    /**
     * Create compound device
     * @param {number} siteId - Site ID
     * @param {Object} compoundData - Compound device data
     * @returns {Promise<Object>} { success: boolean, data: Object|null, error?: string }
     */
    async createCompound(siteId, compoundData) {
      try {
        const response = await api.post(`/v2/sites/${siteId}/compound-devices`, compoundData);
        return { success: true, data: response };
      } catch (error) {
        return { success: false, data: null, error: error.message };
      }
    },
  },

  // ===========================
  // Point Operations
  // ===========================

  points: {
    /**
     * Get all points for a site, equipment, or device
     * @param {number} siteId - Site ID
     * @param {Object} params - Query parameters (equipment_id, device_id, etc.)
     * @returns {Promise<Object>} { success: boolean, data: Array|null, error?: string }
     */
    async getAll(siteId, params = {}) {
      try {
        const response = await api.get(`/v2/sites/${siteId}/points`, params);
        return { success: true, data: response };
      } catch (error) {
        return { success: false, data: null, error: error.message };
      }
    },

    /**
     * Create new point
     * @param {number} siteId - Site ID
     * @param {Object} pointData - Point data
     * @returns {Promise<Object>} { success: boolean, data: Object|null, error?: string }
     */
    async create(siteId, pointData) {
      try {
        const response = await api.post(`/v2/sites/${siteId}/points`, pointData);
        return { success: true, data: response };
      } catch (error) {
        return { success: false, data: null, error: error.message };
      }
    },

    /**
     * Update existing point
     * @param {number} siteId - Site ID
     * @param {number} pointId - Point ID
     * @param {Object} pointData - Updated point data
     * @returns {Promise<Object>} { success: boolean, data: Object|null, error?: string }
     */
    async update(siteId, pointId, pointData) {
      try {
        const response = await api.put(`/v2/sites/${siteId}/points/${pointId}`, pointData);
        return { success: true, data: response };
      } catch (error) {
        return { success: false, data: null, error: error.message };
      }
    },

    /**
     * Delete point
     * @param {number} siteId - Site ID
     * @param {number} pointId - Point ID
     * @param {boolean} cascade - Delete children recursively
     * @returns {Promise<Object>} { success: boolean, error?: string, response?: Object }
     */
    async delete(siteId, pointId, cascade = false) {
      try {
        const params = cascade ? { cascade: true } : {};
        await api.delete(`/v2/sites/${siteId}/points/${pointId}`, params);
        return { success: true };
      } catch (error) {
        // Preserve full error response for cascade delete handling
        return {
          success: false,
          error: error.message,
          response: {
            status: error.status,
            data: error.data,
          },
        };
      }
    },

    /**
     * Bulk create points
     * @param {number} siteId - Site ID
     * @param {Array} pointsData - Array of point data objects
     * @returns {Promise<Object>} { success: boolean, data: Array|null, error?: string }
     */
    async bulkCreate(siteId, pointsData) {
      try {
        const response = await api.post(`/v2/sites/${siteId}/points/bulk`, { points: pointsData });
        return { success: true, data: response };
      } catch (error) {
        return { success: false, data: null, error: error.message };
      }
    },

    /**
     * Duplicate point
     * @param {number} siteId - Site ID
     * @param {number} pointId - Point ID to duplicate
     * @param {string} newName - Name for duplicated point
     * @returns {Promise<Object>} { success: boolean, data: Object|null, error?: string }
     */
    async duplicate(siteId, pointId, newName) {
      try {
        const response = await api.post(`/v2/sites/${siteId}/points/${pointId}/duplicate`, { name: newName });
        return { success: true, data: response };
      } catch (error) {
        return { success: false, data: null, error: error.message };
      }
    },
  },

  // ===========================
  // Hierarchy Operations (Buildings/Levels/Spaces)
  // ===========================

  hierarchy: {
    /**
     * Get complete site hierarchy
     * @param {number} siteId - Site ID
     * @param {boolean} bypassCache - Force fresh data from server
     * @returns {Promise<Object>} { success: boolean, data: Object|null, error?: string }
     */
    async get(siteId, bypassCache = false) {
      try {
        const params = bypassCache ? { bypass_cache: 1 } : {};
        const response = await api.get(`/v2/sites/${siteId}/hierarchy`, params);
        return { success: true, data: response };
      } catch (error) {
        return { success: false, data: null, error: error.message };
      }
    },

    /**
     * Create new building
     * @param {number} siteId - Site ID
     * @param {Object} buildingData - Building data
     * @returns {Promise<Object>} { success: boolean, data: Object|null, error?: string }
     */
    async createBuilding(siteId, buildingData) {
      try {
        const response = await api.post(`/v2/sites/${siteId}/buildings`, buildingData);
        return { success: true, data: response };
      } catch (error) {
        return { success: false, data: null, error: error.message };
      }
    },

    /**
     * Update building
     * @param {number} siteId - Site ID
     * @param {number} buildingId - Building ID
     * @param {Object} buildingData - Updated building data
     * @returns {Promise<Object>} { success: boolean, data: Object|null, error?: string }
     */
    async updateBuilding(siteId, buildingId, buildingData) {
      try {
        const response = await api.put(`/v2/sites/${siteId}/buildings/${buildingId}`, buildingData);
        return { success: true, data: response };
      } catch (error) {
        return { success: false, data: null, error: error.message };
      }
    },

    /**
     * Delete building
     * @param {number} siteId - Site ID
     * @param {number} buildingId - Building ID
     * @param {boolean} cascade - Delete children recursively
     * @returns {Promise<Object>} { success: boolean, error?: string }
     */
    async deleteBuilding(siteId, buildingId, cascade = false) {
      try {
        const params = cascade ? { cascade: true } : {};
        await api.delete(`/v2/sites/${siteId}/buildings/${buildingId}`, params);
        return { success: true };
      } catch (error) {
        return { success: false, error: error.message };
      }
    },

    /**
     * Create new level
     * @param {number} siteId - Site ID
     * @param {number} buildingId - Parent building ID
     * @param {Object} levelData - Level data
     * @returns {Promise<Object>} { success: boolean, data: Object|null, error?: string }
     */
    async createLevel(siteId, buildingId, levelData) {
      try {
        const response = await api.post(`/v2/sites/${siteId}/buildings/${buildingId}/levels`, levelData);
        return { success: true, data: response };
      } catch (error) {
        return { success: false, data: null, error: error.message };
      }
    },

    /**
     * Update level
     * @param {number} siteId - Site ID
     * @param {number} levelId - Level ID
     * @param {Object} levelData - Updated level data
     * @returns {Promise<Object>} { success: boolean, data: Object|null, error?: string }
     */
    async updateLevel(siteId, levelId, levelData) {
      try {
        const response = await api.put(`/v2/sites/${siteId}/levels/${levelId}`, levelData);
        return { success: true, data: response };
      } catch (error) {
        return { success: false, data: null, error: error.message };
      }
    },

    /**
     * Delete level
     * @param {number} siteId - Site ID
     * @param {number} levelId - Level ID
     * @param {boolean} cascade - Delete children recursively
     * @returns {Promise<Object>} { success: boolean, error?: string }
     */
    async deleteLevel(siteId, levelId, cascade = false) {
      try {
        const params = cascade ? { cascade: true } : {};
        // Use v1 route which only requires level ID
        await api.delete(`/levels/${levelId}`, params);
        return { success: true };
      } catch (error) {
        return { success: false, error: error.message };
      }
    },

    /**
     * Create new space
     * @param {number} siteId - Site ID
     * @param {number} levelId - Parent level ID
     * @param {Object} spaceData - Space data
     * @returns {Promise<Object>} { success: boolean, data: Object|null, error?: string }
     */
    async createSpace(siteId, levelId, spaceData) {
      try {
        // v2 API expects building_level_id in the body
        const dataWithLevel = { ...spaceData, building_level_id: levelId };
        const response = await api.post(`/v2/sites/${siteId}/spaces`, dataWithLevel);
        return { success: true, data: response };
      } catch (error) {
        return { success: false, data: null, error: error.message };
      }
    },

    /**
     * Update space
     * @param {number} siteId - Site ID
     * @param {number} spaceId - Space ID
     * @param {Object} spaceData - Updated space data
     * @returns {Promise<Object>} { success: boolean, data: Object|null, error?: string }
     */
    async updateSpace(siteId, spaceId, spaceData) {
      try {
        const response = await api.put(`/v2/sites/${siteId}/spaces/${spaceId}`, spaceData);
        return { success: true, data: response };
      } catch (error) {
        return { success: false, data: null, error: error.message };
      }
    },

    /**
     * Delete space
     * @param {number} siteId - Site ID
     * @param {number} spaceId - Space ID
     * @param {boolean} cascade - Delete children recursively
     * @returns {Promise<Object>} { success: boolean, error?: string }
     */
    async deleteSpace(siteId, spaceId, cascade = false) {
      try {
        const params = cascade ? { cascade: true } : {};
        await api.delete(`/v2/sites/${siteId}/spaces/${spaceId}`, params);
        return { success: true };
      } catch (error) {
        return { success: false, error: error.message };
      }
    },
  },

  // ===========================
  // Template Operations
  // ===========================

  templates: {
    /**
     * Get all equipment templates
     * @returns {Promise<Object>} { success: boolean, data: Array|null, error?: string }
     */
    async getEquipmentTemplates() {
      try {
        const response = await api.get('/v2/equipment-templates');
        return { success: true, data: response };
      } catch (error) {
        return { success: false, data: null, error: error.message };
      }
    },

    /**
     * Get all device templates
     * @returns {Promise<Object>} { success: boolean, data: Array|null, error?: string }
     */
    async getDeviceTemplates() {
      try {
        const response = await api.get('/v2/device-templates');
        return { success: true, data: response };
      } catch (error) {
        return { success: false, data: null, error: error.message };
      }
    },

    /**
     * Get all compound templates
     * @returns {Promise<Object>} { success: boolean, data: Array|null, error?: string }
     */
    async getCompoundTemplates() {
      try {
        const response = await api.get('/compound-equipment/templates');
        return { success: true, data: response };
      } catch (error) {
        return { success: false, data: null, error: error.message };
      }
    },

    /**
     * Create equipment template
     * @param {Object} templateData - Template data
     * @returns {Promise<Object>} { success: boolean, data: Object|null, error?: string }
     */
    async createEquipmentTemplate(templateData) {
      try {
        const response = await api.post('/v2/equipment-templates', templateData);
        return { success: true, data: response };
      } catch (error) {
        return { success: false, data: null, error: error.message };
      }
    },

    /**
     * Create compound template
     * @param {Object} templateData - Template data
     * @returns {Promise<Object>} { success: boolean, data: Object|null, error?: string }
     */
    async createCompoundTemplate(templateData) {
      try {
        const response = await api.post('/compound-equipment/templates', templateData);
        return { success: true, data: response };
      } catch (error) {
        return { success: false, data: null, error: error.message };
      }
    },

    /**
     * Delete template
     * @param {string} templateType - Template type (equipment, device, compound)
     * @param {number} templateId - Template ID
     * @returns {Promise<Object>} { success: boolean, data?: Object, error?: string }
     */
    async delete(templateType, templateId) {
      try {
        let endpoint = '';
        if (templateType === 'compound') {
          endpoint = `/compound-equipment/templates/${templateId}`;
        } else if (templateType === 'equipment') {
          endpoint = `/v2/equipment-templates/${templateId}`;
        } else if (templateType === 'device') {
          endpoint = `/v2/device-templates/${templateId}`;
        } else {
          throw new Error('Invalid template type');
        }

        await api.delete(endpoint);
        return { success: true };
      } catch (error) {
        return {
          success: false,
          error: error.message,
          data: error.data, // Include error data for usage information
        };
      }
    },

    /**
     * Get template usage locations
     * @param {string} templateType - Template type (equipment, device, compound)
     * @param {number} templateId - Template ID
     * @returns {Promise<Object>} { success: boolean, data: Array|null, error?: string }
     */
    async getUsage(templateType, templateId) {
      try {
        let endpoint = '';
        if (templateType === 'compound') {
          endpoint = `/compound-equipment/templates/${templateId}/usage`;
        } else if (templateType === 'equipment') {
          endpoint = `/v2/equipment-templates/${templateId}/usage`;
        } else if (templateType === 'device') {
          endpoint = `/v2/device-templates/${templateId}/usage`;
        } else {
          throw new Error('Invalid template type');
        }

        const response = await api.get(endpoint);
        return { success: true, data: response };
      } catch (error) {
        return { success: false, data: null, error: error.message };
      }
    },
  },

  // ===========================
  // Electrical Operations
  // ===========================

  electrical: {
    /**
     * Get electrical hierarchy
     * @param {number} siteId - Site ID
     * @returns {Promise<Object>} { success: boolean, data: Object|null, error?: string }
     */
    async getHierarchy(siteId) {
      try {
        const response = await api.get(`/sites/${siteId}/electrical-hierarchy`);
        return { success: true, data: response };
      } catch (error) {
        return { success: false, data: null, error: error.message };
      }
    },

    /**
     * Get switchboard types from BRICK Schema
     * @returns {Promise<Object>} { success: boolean, data: Array|null, error?: string }
     */
    async getSwitchboardTypes() {
      try {
        const response = await api.get('/brick-schema/switchboard-types');
        return { success: true, data: response };
      } catch (error) {
        return { success: false, data: null, error: error.message };
      }
    },

    /**
     * Create main switchboard (MSB)
     * @param {number} siteId - Site ID
     * @param {Object} msbData - MSB data
     * @returns {Promise<Object>} { success: boolean, data: Object|null, error?: string }
     */
    async createMSB(siteId, msbData) {
      try {
        const response = await api.post(`/v2/sites/${siteId}/electrical/msb`, msbData);
        return { success: true, data: response };
      } catch (error) {
        return { success: false, data: null, error: error.message };
      }
    },

    /**
     * Create mechanical services switchboard (MSSB)
     * @param {number} siteId - Site ID
     * @param {Object} mssbData - MSSB data
     * @returns {Promise<Object>} { success: boolean, data: Object|null, error?: string }
     */
    async createMSSB(siteId, mssbData) {
      try {
        const response = await api.post(`/v2/sites/${siteId}/electrical/mssb`, mssbData);
        return { success: true, data: response };
      } catch (error) {
        return { success: false, data: null, error: error.message };
      }
    },

    /**
     * Create DDC panel
     * @param {number} siteId - Site ID
     * @param {Object} ddcData - DDC panel data
     * @returns {Promise<Object>} { success: boolean, data: Object|null, error?: string }
     */
    async createDDC(siteId, ddcData) {
      try {
        const response = await api.post(`/v2/sites/${siteId}/electrical/ddc`, ddcData);
        return { success: true, data: response };
      } catch (error) {
        return { success: false, data: null, error: error.message };
      }
    },

    /**
     * Create switchboard
     * @param {number} siteId - Site ID
     * @param {Object} switchboardData - Switchboard data
     * @returns {Promise<Object>} { success: boolean, data: Object|null, error?: string }
     */
    async createSwitchboard(siteId, switchboardData) {
      try {
        const response = await api.post(`/v2/sites/${siteId}/electrical/switchboard`, switchboardData);
        return { success: true, data: response };
      } catch (error) {
        return { success: false, data: null, error: error.message };
      }
    },

    /**
     * Update switchboard
     * @param {number} siteId - Site ID
     * @param {number} switchboardId - Switchboard ID
     * @param {Object} switchboardData - Updated switchboard data
     * @returns {Promise<Object>} { success: boolean, data: Object|null, error?: string }
     */
    async updateSwitchboard(siteId, switchboardId, switchboardData) {
      try {
        const response = await api.put(`/v2/sites/${siteId}/electrical/switchboard/${switchboardId}`, switchboardData);
        return { success: true, data: response };
      } catch (error) {
        return { success: false, data: null, error: error.message };
      }
    },

    /**
     * Delete electrical node
     * @param {number} siteId - Site ID
     * @param {string} nodeType - Node type (msb, mssb, ddc, switchboard)
     * @param {number} nodeId - Node ID
     * @returns {Promise<Object>} { success: boolean, error?: string }
     */
    async deleteNode(siteId, nodeType, nodeId) {
      try {
        await api.delete(`/v2/sites/${siteId}/electrical/${nodeType.toLowerCase()}/${nodeId}`);
        return { success: true };
      } catch (error) {
        return { success: false, error: error.message };
      }
    },

    /**
     * Allocate equipment to DDC controller
     * @param {number} siteId - Site ID
     * @param {Object} allocationData - DDC allocation data
     * @returns {Promise<Object>} { success: boolean, data: Object|null, error?: string }
     */
    async allocateToDdc(siteId, allocationData) {
      try {
        const response = await api.post(`/v2/sites/${siteId}/electrical/ddc-allocation`, allocationData);
        return { success: true, data: response };
      } catch (error) {
        return { success: false, data: null, error: error.message };
      }
    },

    /**
     * Save power connection
     * @param {number} siteId - Site ID
     * @param {Object} connectionData - Power connection data
     * @returns {Promise<Object>} { success: boolean, data: Object|null, error?: string }
     */
    async savePowerConnection(siteId, connectionData) {
      try {
        const response = await api.post(`/v2/sites/${siteId}/electrical/power-connection`, connectionData);
        return { success: true, data: response };
      } catch (error) {
        return { success: false, data: null, error: error.message };
      }
    },

    /**
     * Save switchboard classification
     * @param {number} siteId - Site ID
     * @param {number} switchboardId - Switchboard ID
     * @param {Object} classificationData - Classification data
     * @returns {Promise<Object>} { success: boolean, data: Object|null, error?: string }
     */
    async saveSwitchboardClassification(siteId, switchboardId, classificationData) {
      try {
        const response = await api.post(`/v2/sites/${siteId}/electrical/switchboard/${switchboardId}/classification`, classificationData);
        return { success: true, data: response };
      } catch (error) {
        return { success: false, data: null, error: error.message };
      }
    },
  },

  // ===========================
  // Export Operations
  // ===========================

  export: {
    /**
     * Get BRICK TTL export
     * @param {number} siteId - Site ID
     * @returns {Promise<Object>} { success: boolean, data: string|null, error?: string }
     */
    async getTTL(siteId) {
      try {
        const response = await fetch(`/api/v2/sites/${siteId}/brick/ttl`);
        const result = await response.json();

        if (!result.success || !result.data?.ttl) {
          throw new Error('Invalid TTL response from server');
        }

        return { success: true, data: result.data.ttl };
      } catch (error) {
        return { success: false, data: null, error: error.message };
      }
    },

    /**
     * Get BRICK JSON export
     * @param {number} siteId - Site ID
     * @returns {Promise<Object>} { success: boolean, data: Object|null, error?: string }
     */
    async getJSON(siteId) {
      try {
        const response = await api.get(`/v2/sites/${siteId}/brick/json`);
        return { success: true, data: response };
      } catch (error) {
        return { success: false, data: null, error: error.message };
      }
    },
  },
};

export default brickSchemaService;
